import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { FormProvider, useForm } from 'react-hook-form';

import InteractiveContentModal, { InteractiveContentModalProps } from '../../../atoms/dialog/interactiveContentModal';
import { StyleButton, HeaderTitleInteractionBox } from '../style';

import FillInBlankForm, { InteractiveFillBlankFields } from './fillInBlankForm';

import PlayCircleIcon from '@components/atoms/icon/playCircleIcon';
import RedoIcon from '@components/atoms/icon/redoIcon';
import {
  IAnswer,
  IInteractiveContent,
  InteractionType,
  IQuiz,
  SubmitInteractionAnswerResponse,
  IInteractiveContentSubmissions,
} from '@interfaces/learning';
import InteractiveContentActions from '@redux/interactiveContent/actions';
import { RootState } from '@redux/reducer';
import theme from 'src/theme';

export type InteractiveFillBlankProps = Pick<InteractiveContentModalProps, 'open' | 'containerRef' | 'onClose'> & {
  interactiveContent: IInteractiveContent;
  isPreview?: boolean;
};

const InteractiveFillBlank = ({
  open,
  onClose,
  interactiveContent,
  containerRef,
  isPreview,
}: InteractiveFillBlankProps) => {
  const content = interactiveContent.content as IQuiz;
  const { name, question_sets } = content;
  const questionSet = question_sets?.[0];
  const question = questionSet?.question_sets_questions?.[0]?.content;

  const dispatch = useDispatch();
  const {
    interactiveContentSubmission,
    interactiveContentAnswerDetail,
    interactionResultSubmit,
    isInteractiveContentSubmitSuccess,
    isInteractiveContentSubmissionLoading,
  } = useSelector((state: RootState) => state.InteractContent);

  const { t } = useTranslation();

  const [answers, setAnswers] = useState<IAnswer[] | null>(null);
  const [maxScore, setMaxScore] = useState(0);
  const [score, setScore] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [isExaminedAnswer, setIsExaminedAnswer] = useState(false);

  const userAnswers =
    interactionResultSubmit?.detail.content[0].user_answers || interactiveContentAnswerDetail?.content[0].user_answers;

  const methods = useForm<InteractiveFillBlankFields>({
    defaultValues: {
      answers: {},
    },
    mode: 'onChange',
  });

  const {
    reset,
    getValues,
    handleSubmit: formHandleSubmit,
    formState: { isSubmitted, errors, isValid, isSubmitting },
  } = methods;

  const startInteractionContent = async () => {
    if (!open && isInteractiveContentSubmissionLoading) return;

    if (!isPreview && open && !isInteractiveContentSubmissionLoading && !interactiveContentSubmission) {
      const started_at = new Date().toISOString();
      const payload = {
        type: InteractionType.FILL_IN_THE_BLANK,
        question_set_id: questionSet?.id,
        started_at,
      };
      setIsExaminedAnswer(false);
      dispatch(InteractiveContentActions.start_interactive_content(interactiveContent.id, payload));
    }

    if (isPreview) {
      dispatch(InteractiveContentActions.clean_interactive_content_submission());
    }
  };

  const retryInteraction = () => {
    startInteractionContent();
    reset();
    setShowAnswer(false);
  };

  const handleSubmit = async (data: InteractiveFillBlankFields) => {
    let ans: IAnswer[];
    const total_score = Object.keys(data.answers).length;
    const payload = {
      type: InteractionType.FILL_IN_THE_BLANK,
      question_set_id: questionSet?.id,
      finished_at: new Date().toISOString(),
      answers: [
        {
          question_id: question?.id,
          user_answers: Object.entries(data.answers).map(([key, value]) => ({
            group_name: key,
            answer_text: value.trim(),
          })),
        },
      ],
    };

    if (!isPreview && interactiveContentSubmission?.id) {
      dispatch(
        InteractiveContentActions.submit_interactive_content(
          interactiveContent.id,
          interactiveContentSubmission.id,
          payload,
        ),
      );
      ans = [];
    } else {
      dispatch(InteractiveContentActions.send_answer_interactive_content(interactiveContent.id, payload));
      ans = question?.answers || [];
    }

    setAnswers(ans);
    setMaxScore(total_score);
  };

  const renderHeaderTitle = () => {
    if (isSubmitted && isExaminedAnswer && isInteractiveContentSubmitSuccess) {
      if (errors.answers) {
        return (
          <HeaderTitleInteractionBox>
            <img src="/static/icons/wrong-retry-icon.svg" className="svg-icon" />
            <Box style={{ marginLeft: '16px' }}>
              <Typography component="div" fontWeight={500} fontSize={24} color="#000000DE">
                {t('interactive.fill_blank.fail.score', { total_correct_answers: score, total_answers: maxScore })}
              </Typography>
              <Typography fontSize={{ xs: 36, md: 48 }} fontWeight={700} lineHeight="43px" mb={3} color="#000000DE">
                {t('interactive.fill_blank.fail.title')}
              </Typography>
            </Box>
          </HeaderTitleInteractionBox>
        );
      }
      return (
        <HeaderTitleInteractionBox mb={3}>
          <img src="/static/icons/correct-icon.svg" className="svg-icon" />
          <Box style={{ marginLeft: '16px' }}>
            <Typography component="div" fontWeight={500} fontSize={24} color="#000000DE">
              {t('interactive.fill_blank.success.score', { total_correct_answers: score, total_answers: maxScore })}
            </Typography>
            <Typography fontSize={{ xs: 36, md: 48 }} fontWeight={700} lineHeight="43px" color="#000000DE">
              {t('interactive.fill_blank.success.title')}
            </Typography>
          </Box>
        </HeaderTitleInteractionBox>
      );
    }

    return (
      <Typography id="fill-blank-rule" fontWeight={500} fontSize={18} color={theme.palette.text.secondary}>
        {t('interactive.fill_blank.rule')}
      </Typography>
    );
  };

  const renderFooterButtons = () => {
    if (!isSubmitted) {
      return (
        <StyleButton variant="contained" type="submit" disabled={!isValid || isSubmitting}>
          {isSubmitting ? (
            <CircularProgress size="20px" sx={{ mr: 1.5 }} />
          ) : (
            <CheckCircleOutlineIcon sx={{ mr: 1.5 }} />
          )}
          {t('interactive.fill_blank.button.submit')}
        </StyleButton>
      );
    }

    if (errors.answers) {
      return (
        <>
          <StyleButton variant="outlined" onClick={retryInteraction}>
            <RedoIcon sx={{ mr: 1.5, fontSize: '30px' }} />
            {t('interactive.fill_blank.button.reset')}
          </StyleButton>
          <StyleButton variant="contained" onClick={onClose}>
            <PlayCircleIcon sx={{ mr: 1.5, fontSize: '32px' }} />
            {t('interactive.fill_blank.button.continue')}
          </StyleButton>
        </>
      );
    }

    return (
      <StyleButton variant="contained" onClick={onClose} disabled={isInteractiveContentSubmissionLoading}>
        <PlayCircleIcon sx={{ mr: 1.5, fontSize: '30px' }} />
        {t('interactive.fill_blank.button.continue')}
      </StyleButton>
    );
  };

  useEffect(() => {
    const detail = interactionResultSubmit?.detail || interactiveContentAnswerDetail;

    if (isInteractiveContentSubmitSuccess && detail) {
      const ans = detail.content[0]?.answers;
      const userAnswers = detail.content[0]?.user_answers;
      const correctAnswerCount = detail.content[0]?.result_score;

      userAnswers.forEach((userAnswer) => {
        if (!userAnswer.is_correct)
          return methods.setError(`answers.${userAnswer.group_name}`, { type: 'manual', message: 'Incorrect answer' });
      });

      setIsExaminedAnswer(true);
      setAnswers(ans);
      setScore(correctAnswerCount);
    }
  }, [isInteractiveContentSubmitSuccess, interactiveContentAnswerDetail]);

  useEffect(() => {
    startInteractionContent();
  }, [open]);

  return (
    <InteractiveContentModal open={open} containerRef={containerRef} onClose={onClose}>
      <>
        <Box sx={{ position: 'absolute', top: 10, right: 10 }}>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        {!question ? (
          <Typography>Failed to load question</Typography>
        ) : (
          <FormProvider {...methods}>
            <Box component="form" height="100%" onSubmit={formHandleSubmit(handleSubmit)}>
              <Box
                p={5}
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
                height="100%"
                overflow="auto"
              >
                <Box display="flex" flexDirection="column">
                  {renderHeaderTitle()}
                  <FillInBlankForm
                    isLoading={isInteractiveContentSubmissionLoading}
                    content={content}
                    showAnswer={showAnswer}
                    answers={answers}
                    userAnswers={userAnswers}
                  />
                </Box>
                <Box
                  display="flex"
                  width="100%"
                  alignItems="center"
                  mt={5}
                  flexDirection={{
                    md: 'row',
                    xs: 'column',
                  }}
                  gap={2}
                >
                  {isSubmitted && errors.answers && (
                    <StyleButton
                      variant="text"
                      onClick={() => {
                        setShowAnswer((prevState) => !prevState);
                      }}
                      sx={{
                        [theme.breakpoints.down('md')]: {
                          width: '100%',
                        },
                      }}
                    >
                      {showAnswer ? (
                        <>
                          <VisibilityOffOutlinedIcon sx={{ mr: 0.5, fontSize: '22px' }} />
                          {t('interactive.fill_blank.button.hide_answer')}
                        </>
                      ) : (
                        <>
                          <VisibilityOutlinedIcon sx={{ mr: 0.5, fontSize: '22px' }} />
                          {t('interactive.fill_blank.button.view_answer')}
                        </>
                      )}
                    </StyleButton>
                  )}
                  <Box
                    display="flex"
                    ml="auto"
                    sx={{
                      gap: 5,
                      [theme.breakpoints.down('lg')]: {
                        gap: 3,
                      },
                      [theme.breakpoints.down('md')]: {
                        flexDirection: 'column',
                        width: '100%',
                        gap: 2,
                      },
                    }}
                  >
                    {renderFooterButtons()}
                  </Box>
                </Box>
              </Box>
            </Box>
          </FormProvider>
        )}
      </>
    </InteractiveContentModal>
  );
};

export default InteractiveFillBlank;
